import React, { useState } from 'react';
import { Calendar, Zap, FileText, CheckCircle, Star, ArrowRight, Building2, Users, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CTAStrip } from '../components/CTAStrip';
const caseStudies = [{
  icon: Building2,
  title: 'London Borough Council',
  type: 'Council Estate Maintenance',
  desc: 'Ongoing monthly contract for estate clearances across 12 council properties. Reduced costs by 30% vs previous provider.',
  result: '£40k saved annually'
}, {
  icon: Users,
  title: 'Premier Property Management',
  type: 'Property Management',
  desc: 'Rapid response clearance service for void properties. Average turnaround reduced from 5 days to 24 hours.',
  result: '80% faster turnaround'
}, {
  icon: Briefcase,
  title: 'Greenfield Developments',
  type: 'Commercial Developer',
  desc: 'Site waste management across 3 active construction sites. Full compliance documentation provided monthly.',
  result: '100% compliance record'
}];
const pricingModels = [{
  title: 'Per Collection',
  price: 'From £80',
  desc: 'Pay only when you need us. Perfect for occasional clearances or one-off jobs.',
  features: ['No minimum commitment', 'Flexible scheduling', 'Same-day available', 'Full documentation']
}, {
  title: 'Monthly Contract',
  price: 'From £250/mo',
  desc: 'Regular scheduled collections at a fixed monthly rate. Best for ongoing waste needs.',
  features: ['Priority booking', 'Dedicated account manager', 'Monthly reporting', 'Discounted rates'],
  highlight: true
}, {
  title: 'Volume-Based',
  price: 'Custom pricing',
  desc: 'Tailored pricing for high-volume clients. The more you use us, the more you save.',
  features: ['Volume discounts', 'Multi-site management', 'Bespoke SLA', 'Compliance support']
}];
const benefits = ['Priority booking — jump the queue for urgent jobs', 'Dedicated account contact — one person who knows your needs', 'Discount system for long-term agreements', 'Full compliance documentation and waste transfer notes', 'Monthly eco-reporting — track your recycling impact', 'Flexible contract terms — no long lock-ins'];
export function ContractsPage() {
  const [form, setForm] = useState({
    name: '',
    org: '',
    email: '',
    phone: '',
    requirements: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div className="pt-16 lg:pt-20" data-id="element-404">
      {/* Hero */}
      <section className="py-20 px-4" style={{
      background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
    }} data-id="element-405">
        <div className="max-w-7xl mx-auto text-center" data-id="element-406">
          <p className="text-green-300 text-sm font-bold uppercase tracking-widest mb-3" data-id="element-407">
            For Councils & Businesses
          </p>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-408">
            Reliable Long-Term Waste & Clearance Partner
          </h1>
          <p className="text-green-100 text-xl max-w-3xl mx-auto mb-8" data-id="element-409">
            Trusted by councils, estate agents, landlords, and property managers
            across London. Flexible contracts, priority service, full
            compliance.
          </p>
          <a href="#enquiry" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-[#1B8F4B] bg-white hover:bg-green-50 transition-colors" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-410">
            Request Partnership Call <ArrowRight className="w-4 h-4" data-id="element-411" />
          </a>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 px-4 bg-white" data-id="element-412">
        <div className="max-w-7xl mx-auto" data-id="element-413">
          <div className="text-center mb-12" data-id="element-414">
            <h2 className="text-3xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-415">
              What We Offer Partners
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-416">
            {[{
            icon: Calendar,
            title: 'Scheduled Collections',
            desc: 'Regular, reliable waste collections on a schedule that works for your organisation. Weekly, fortnightly, or monthly.'
          }, {
            icon: Zap,
            title: 'Rapid Response Clear-Outs',
            desc: 'Emergency clearances when you need them most. We can mobilise within hours for urgent void property clearances.'
          }, {
            icon: FileText,
            title: 'Documentation & Compliance',
            desc: 'Full waste transfer notes, duty of care documentation, and monthly compliance reports for your records.'
          }].map(({
            icon: Icon,
            title,
            desc
          }) => <div key={title} className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300" data-id="element-417">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{
              backgroundColor: '#F0FDF4'
            }} data-id="element-418">
                  <Icon className="w-8 h-8" style={{
                color: '#1B8F4B'
              }} data-id="element-419" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-420">
                  {title}
                </h3>
                <p className="text-gray-500 leading-relaxed" data-id="element-421">{desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 px-4 texture-paper" data-id="element-422">
        <div className="max-w-7xl mx-auto" data-id="element-423">
          <div className="text-center mb-12" data-id="element-424">
            <h2 className="text-3xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-425">
              Case Studies
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-426">
              Real partnerships, real results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-427">
            {caseStudies.map(({
            icon: Icon,
            title,
            type,
            desc,
            result
          }) => <div key={title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-428">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
              backgroundColor: '#F0FDF4'
            }} data-id="element-429">
                  <Icon className="w-6 h-6" style={{
                color: '#1B8F4B'
              }} data-id="element-430" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider" style={{
              color: '#1B8F4B'
            }} data-id="element-431">
                  {type}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-432">
                  {title}
                </h3>
                <p className="text-gray-500 text-sm mb-4" data-id="element-433">{desc}</p>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{
              backgroundColor: '#F0FDF4'
            }} data-id="element-434">
                  <Star className="w-4 h-4" style={{
                color: '#1B8F4B'
              }} data-id="element-435" />
                  <span className="text-sm font-bold" style={{
                color: '#1B8F4B'
              }} data-id="element-436">
                    {result}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Models */}
      <section className="py-20 px-4 bg-white" data-id="element-437">
        <div className="max-w-7xl mx-auto" data-id="element-438">
          <div className="text-center mb-12" data-id="element-439">
            <h2 className="text-3xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-440">
              Pricing Models
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-441">
              Flexible options to suit every organisation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-442">
            {pricingModels.map(({
            title,
            price,
            desc,
            features,
            highlight
          }) => <div key={title} className={`rounded-2xl p-8 border-2 transition-all duration-300 ${highlight ? 'border-[#1B8F4B] shadow-xl' : 'border-gray-100 hover:border-[#1B8F4B] hover:shadow-lg'}`} style={highlight ? {
            background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
          } : {}} data-id="element-443">
                  {highlight && <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-white mb-4" style={{
              color: '#1B8F4B'
            }} data-id="element-444">
                      Most Popular
                    </span>}
                  <h3 className={`text-xl font-bold mb-1 ${highlight ? 'text-white' : 'text-gray-900'}`} style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-445">
                    {title}
                  </h3>
                  <p className={`text-3xl font-extrabold mb-3 ${highlight ? 'text-white' : ''}`} style={{
              fontFamily: 'Montserrat, sans-serif',
              color: highlight ? undefined : '#1B8F4B'
            }} data-id="element-446">
                    {price}
                  </p>
                  <p className={`text-sm mb-5 ${highlight ? 'text-green-100' : 'text-gray-500'}`} data-id="element-447">
                    {desc}
                  </p>
                  <ul className="space-y-2" data-id="element-448">
                    {features.map(f => <li key={f} className={`flex items-center gap-2 text-sm ${highlight ? 'text-green-100' : 'text-gray-600'}`} data-id="element-449">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 ${highlight ? 'text-green-300' : ''}`} style={!highlight ? {
                  color: '#1B8F4B'
                } : {}} data-id="element-450" />
                        {f}
                      </li>)}
                  </ul>
                </div>)}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-451">
        <div className="max-w-4xl mx-auto text-center" data-id="element-452">
          <h2 className="text-3xl font-bold text-gray-900 mb-8" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-453">
            Contract Benefits
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-454">
            {benefits.map(b => <div key={b} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100 text-left" data-id="element-455">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: '#1B8F4B'
            }} data-id="element-456" />
                <span className="text-gray-700 text-sm" data-id="element-457">{b}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry" className="py-20 px-4 texture-paper" data-id="element-458">
        <div className="max-w-2xl mx-auto" data-id="element-459">
          <div className="text-center mb-10" data-id="element-460">
            <h2 className="text-3xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-461">
              Request a Partnership Call
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-462">
              Tell us about your needs and we'll be in touch within 30 minutes.
            </p>
          </div>
          {submitted ? <div className="bg-white rounded-2xl p-10 text-center shadow-sm border border-gray-100" data-id="element-463">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{
            color: '#1B8F4B'
          }} data-id="element-464" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-465">
                Thank You!
              </h3>
              <p className="text-gray-500" data-id="element-466">
                We'll be in touch within 30 minutes to discuss your
                requirements.
              </p>
            </div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-5" data-id="element-467">
              {[{
            label: 'Your Name',
            key: 'name',
            type: 'text',
            placeholder: 'John Smith'
          }, {
            label: 'Organisation',
            key: 'org',
            type: 'text',
            placeholder: 'Company or Council name'
          }, {
            label: 'Email Address',
            key: 'email',
            type: 'email',
            placeholder: 'john@example.com'
          }, {
            label: 'Phone Number',
            key: 'phone',
            type: 'tel',
            placeholder: '07405 641227'
          }].map(({
            label,
            key,
            type,
            placeholder
          }) => <div key={key} data-id="element-468">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-469">
                    {label}
                  </label>
                  <input type={type} placeholder={placeholder} value={form[key as keyof typeof form]} onChange={e => setForm({
              ...form,
              [key]: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#1B8F4B] focus:ring-2 focus:ring-[#1B8F4B]/20 transition-colors text-sm" data-id="element-470" />
                </div>)}
              <div data-id="element-471">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-472">
                  Requirements
                </label>
                <textarea rows={4} placeholder="Tell us about your waste management needs..." value={form.requirements} onChange={e => setForm({
              ...form,
              requirements: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#1B8F4B] focus:ring-2 focus:ring-[#1B8F4B]/20 transition-colors text-sm resize-none" data-id="element-473" />
              </div>
              <button type="submit" className="w-full py-4 rounded-xl font-bold text-white text-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg" style={{
            backgroundColor: '#1B8F4B',
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-474">
                Request Partnership Call
              </button>
            </form>}
        </div>
      </section>

      <CTAStrip data-id="element-475" />
    </div>;
}